<template>
    <table class="w-full">
        <thead>
            <tr class="text-left text-gray-400">
                <th class="w-10 py-1.5"></th>
                <th class="w-10 py-1.5 pr-2">#</th>
                <th class="py-1.5 pr-3">
                    <button type="button" class="inline-flex items-center gap-1 hover:text-gray-600" @click="onHeaderClicked(startHeader)">
                        {{ $t('views.monitoring.groupOverview.modals.backfill.table.headings.start') }}
                        <Icon class="h-3.5 w-2" :src="getHeaderSortIcon(startHeader).icon"></Icon>
                    </button>
                </th>
                <th class="py-1.5 pr-3">{{ $t('views.monitoring.groupOverview.modals.backfill.table.headings.end') }}</th>
                <th class="py-1.5 pr-3">{{ $t('views.monitoring.groupOverview.modals.backfill.table.headings.duration') }}</th>
                <th class="py-1.5 pr-3">{{ $t('views.monitoring.groupOverview.modals.backfill.table.headings.source') }}</th>
                <th class="py-1.5 pr-3" colspan="2">
                    <span class="inline-flex items-center gap-1">
                        {{ $t('views.monitoring.groupOverview.modals.backfill.table.headings.confidence') }}
                        <TooltipProvider :delay-duration="0">
                            <Tooltip>
                                <TooltipTrigger as-child>
                                    <button type="button" class="rounded-full p-1 transition-colors hover:bg-gray-100">
                                        <span class="sr-only">{{ $t('views.monitoring.groupOverview.modals.backfill.table.confidenceExplanation') }}</span>
                                        <InfoIcon class="size-4" aria-hidden="true" />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent class="max-w-64" :collision-padding="12">
                                    <p>{{ $t('views.monitoring.groupOverview.modals.backfill.table.confidenceExplanation') }}</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </span>
                </th>
            </tr>
        </thead>
        <tbody>
            <template v-for="{ suggestion, number } in displayedSuggestions" :key="suggestion.id">
                <tr
                    class="border-t border-gray-100"
                    :class="{ 'opacity-50': !suggestion.accepted, 'border-l-2 border-l-red-400 bg-red-50': isBlocked(suggestion.id) }"
                >
                    <td class="py-1.5 align-top">
                        <CustomCheckbox
                            :checked="suggestion.accepted"
                            tabbable
                            :disabled="disabled"
                            :label="$t('views.monitoring.groupOverview.modals.backfill.table.acceptRow', { number })"
                            label-class="sr-only"
                            @update:checked="(value) => emit('setAccepted', suggestion.id, value)"
                        />
                    </td>
                    <td class="py-1.5 pr-2 align-top font-medium text-gray-500">{{ number }}</td>
                    <td class="w-44 py-1.5 pr-3 align-top">
                        <DatePicker
                            :value="toPickerValue(suggestion.start)"
                            :resetable="false"
                            :aria-label="$t('views.monitoring.groupOverview.modals.backfill.table.headings.start')"
                            :max="today"
                            position="above"
                            :show-overlay="false"
                            :disabled="disabled || !suggestion.accepted"
                            @update:value="(value) => emit('setStart', suggestion.id, formatDate(value))"
                        />
                    </td>
                    <td class="w-44 py-1.5 pr-3 align-top">
                        <DatePicker
                            :value="suggestion.end ? toPickerValue(suggestion.end) : ''"
                            :resetable="isLatest(suggestion)"
                            :aria-label="$t('views.monitoring.groupOverview.modals.backfill.table.headings.end')"
                            :min="dayAfter(suggestion.start)"
                            :max="today"
                            position="above"
                            :show-overlay="false"
                            :disabled="disabled || !suggestion.accepted"
                            @update:value="(value) => emit('setEnd', suggestion.id, value ? formatDate(value) : null)"
                        />
                        <p v-if="!suggestion.end" class="mt-0.5 text-[10px] italic text-gray-400">{{ $t('views.monitoring.groupOverview.modals.backfill.table.ongoing') }}</p>
                    </td>
                    <td class="py-1.5 pr-3 align-top whitespace-nowrap">{{ formatDuration(durationDays(suggestion.start, suggestion.end || today)) }}</td>
                    <td class="py-1.5 pr-3 align-top">
                        <Badge :variant="sourceBadgeVariant(suggestion.source)">{{ $t(`views.monitoring.groupOverview.modals.backfill.sources.${suggestion.source}`) }}</Badge>
                    </td>
                    <td class="py-1.5 pr-3 align-top">
                        <Badge v-if="suggestion.confidence" variant="outline" :class="confidenceBadgeClass(suggestion.confidence)">
                            {{ $t(`views.monitoring.groupOverview.modals.backfill.confidence.${suggestion.confidence}`) }}
                        </Badge>
                        <span v-else class="text-gray-300">—</span>
                    </td>
                    <td class="py-1.5 align-top text-right">
                        <button
                            type="button"
                            class="rounded-full p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-red-600 disabled:cursor-default disabled:opacity-50"
                            :disabled="disabled"
                            :aria-label="$t('views.monitoring.groupOverview.modals.backfill.table.discard')"
                            @click="emit('discard', suggestion.id)"
                        >
                            <Trash2 class="size-4" aria-hidden="true" />
                        </button>
                    </td>
                </tr>
                <tr v-if="invalidRangeIds.has(suggestion.id)" class="border-l-2 border-red-400 bg-red-50">
                    <td></td>
                    <td colspan="7" class="py-1.5 pr-4 text-xs font-medium text-red-800">{{ $t('views.monitoring.groupOverview.modals.backfill.table.invalidRangeWarning') }}</td>
                </tr>
                <tr v-else-if="overlappingIds.has(suggestion.id)" class="border-l-2 border-red-400 bg-red-50">
                    <td></td>
                    <td colspan="7" class="py-1.5 pr-4 text-xs font-medium text-red-800">{{ $t('views.monitoring.groupOverview.modals.backfill.table.overlapWarning') }}</td>
                </tr>
                <tr v-if="suggestion.note" class="border-l-2 border-amber-400 bg-amber-50">
                    <td></td>
                    <td colspan="7" class="py-1.5 pr-4 text-xs text-amber-800">{{ suggestion.note }}</td>
                </tr>
            </template>
        </tbody>
    </table>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { addDays, format, parseISO } from 'date-fns';
import { Trash2 } from 'lucide-vue-next';
import DatePicker from '@/components/ui/DatePicker.vue';
import CustomCheckbox from '@/components/ui/CustomCheckbox.vue';
import Badge from '@/components/ui/badge/Badge.vue';
import InfoIcon from '@/components/icons/InfoIcon.vue';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import useSortableTable from '@/composables/useSortableTable';
import { ProductionGroup, type TableHeader } from '@/types';
import {
    durationDays,
    formatDuration,
    invalidRangeSuggestionIds,
    overlappingSuggestionIds,
    type BackfillDraft,
    type BackfillDraftConfidence,
    type BackfillDraftSource,
} from './useProductionGroupBackfill';

const props = defineProps<{
    suggestions: BackfillDraft[];
    existingGroups: ProductionGroup[];
    today: string;
    // True while the wizard's own "Create" requests are in flight - every row control is disabled
    // then, since an edit or discard mid-creation would target rows the in-flight requests already
    // captured a snapshot of (see BackfillWizardModal.vue's handleCreateAll).
    disabled?: boolean;
}>();

const emit = defineEmits<{
    setAccepted: [id: string, accepted: boolean];
    discard: [id: string];
    setStart: [id: string, start: string];
    setEnd: [id: string, end: string | null];
}>();

// "#" stays each suggestion's original detection-order number regardless of sort direction - it is
// the same number the groups-lane chart bands are labelled with (BackfillTimeline builds its band
// labels off this same array's order), so a row must keep its number when sorting reorders the rows
// around it. Numbering first and sorting the numbered array (rather than sorting suggestions and
// numbering after) is what keeps that number pinned to its row.
const numberedSuggestions = computed(() => props.suggestions.map((suggestion, index) => ({ suggestion, number: index + 1 })));

// Same sort-cycle composable (asc -> desc -> off) every other sortable table header in the app uses.
// ISO ("yyyy-MM-dd") start dates sort correctly as plain strings under its default comparator.
const startHeader: TableHeader = { text: '', value: 'suggestion.start', sortable: true };
const { sortedItems: displayedSuggestions, getHeaderSortIcon, onHeaderClicked } = useSortableTable(numberedSuggestions, ref([startHeader]));

const overlappingIds = computed(() => overlappingSuggestionIds(props.suggestions, props.existingGroups, props.today));
const invalidRangeIds = computed(() => invalidRangeSuggestionIds(props.suggestions));

function isBlocked(id: string) {
    return invalidRangeIds.value.has(id) || overlappingIds.value.has(id);
}

function dayAfter(date: string) {
    return format(addDays(parseISO(date), 1), 'yyyy-MM-dd');
}

const latestStart = computed(() => Math.max(...props.suggestions.map((s) => Date.parse(s.start))));

// End is only ever editable to "ongoing" (blank) on the most recent suggestion - an earlier one
// left open-ended would silently overlap whatever comes after it.
function isLatest(suggestion: BackfillDraft) {
    return Date.parse(suggestion.start) === latestStart.value;
}

function formatDate(value: string) {
    return format(parseISO(value), 'yyyy-MM-dd');
}

// The inverse of formatDate: DatePicker displays its `value` via `new Date(value)`, which reads a
// bare "yyyy-MM-dd" as UTC midnight - the wrong calendar day once formatted back in a negative-offset
// timezone. parseISO instead reads a date-only string as local midnight, so round-tripping it through
// toISOString() hands DatePicker a timestamp that displays as the same local day it was given.
function toPickerValue(dateOnly: string): string {
    return parseISO(dateOnly).toISOString();
}

function sourceBadgeVariant(source: BackfillDraftSource) {
    return source === 'Manual' ? 'outline' : 'secondary';
}

// Same green/amber/red reading as the data-quality badges elsewhere in the app (see
// qualityCategoryStyles.ts) - "outline" gives a transparent base to layer these over, since the
// other variants all carry their own background.
const CONFIDENCE_BADGE_CLASS: Record<BackfillDraftConfidence, string> = {
    High: 'border-transparent bg-green-100 text-green-700',
    Medium: 'border-transparent bg-yellow-100 text-yellow-700',
    Low: 'border-transparent bg-red-100 text-red-700',
};

function confidenceBadgeClass(confidence: BackfillDraftConfidence) {
    return CONFIDENCE_BADGE_CLASS[confidence];
}
</script>
